import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EdgeLinkStatusResponse, EdgeLinkStatus } from "@/lib/api/edgelink";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { AlertCircle, CheckCircle2, XCircle, Clock, Wifi, WifiOff, Activity, AlertTriangle } from "lucide-react";

export default function TunnelStatusCard({ status, isLoading }: { status?: EdgeLinkStatusResponse; isLoading: boolean }) {
  if (isLoading) {
    return (
      <Card className="border-muted/40">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!status) {
    return (
      <Card className="border-yellow-500/30 bg-gradient-to-br from-yellow-500/5 to-transparent">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-500/10">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
            </div>
            <div>
              <p className="font-semibold text-yellow-400">Unable to fetch status</p>
              <p className="text-sm text-muted-foreground">Could not retrieve connection status from Cloudflare</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const unknown_status = {
    icon: AlertCircle,
    label: 'Unknown',
    description: 'Unable to determine tunnel status',
    bgClass: 'bg-gradient-to-br from-yellow-500/10 to-transparent',
    borderClass: 'border-yellow-500/30',
    iconClass: 'bg-yellow-500/20 text-yellow-400',
    badgeClass: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
  }

  const statusConfig = {
    [EdgeLinkStatus.HEALTHY]: {
      icon: CheckCircle2,
      label: 'Connected',
      description: 'Tunnel is active and healthy',
      bgClass: 'bg-gradient-to-br from-emerald-500/10 to-transparent',
      borderClass: 'border-emerald-500/30',
      iconClass: 'bg-emerald-500/20 text-emerald-400',
      badgeClass: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    },
    [EdgeLinkStatus.DOWN]: {
      icon: XCircle,
      label: 'Disconnected',
      description: 'Tunnel is currently offline',
      bgClass: 'bg-gradient-to-br from-red-500/10 to-transparent',
      borderClass: 'border-red-500/30',
      iconClass: 'bg-red-500/20 text-red-400',
      badgeClass: 'bg-red-500/10 text-red-400 border-red-500/30',
    },
    [EdgeLinkStatus.INACTIVE]: {
      icon: Clock,
      label: 'Inactive',
      description: 'Tunnel has never been connected',
      bgClass: 'bg-gradient-to-br from-zinc-500/10 to-transparent',
      borderClass: 'border-zinc-500/30',
      iconClass: 'bg-zinc-500/20 text-zinc-400',
      badgeClass: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30',
    },
    [EdgeLinkStatus.UNKNOWN]: unknown_status
  }[status.status] || unknown_status;

  const StatusIcon = statusConfig.icon;
  const hasMultipleConnections = status.connections.length > 1;
  const isConnected = status.status === EdgeLinkStatus.HEALTHY;
  const hasConnections = status.connections.length > 0;

  return (
    <Card className={cn(statusConfig.borderClass, statusConfig.bgClass)}>
      <CardContent className="p-4">
        {/* Main content grid: Status | HA Warning (optional) | Connections */}
        <div className={cn(
          "grid gap-4 items-center",
          hasMultipleConnections
            ? "grid-cols-1 lg:grid-cols-[1fr_auto_auto]"
            : hasConnections
              ? "grid-cols-1 lg:grid-cols-[1fr_auto]"
              : "grid-cols-1"
        )}>
          {/* Left: Status info */}
          <div className="flex items-center gap-3">
            <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', statusConfig.iconClass)}>
              <StatusIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-bold">{statusConfig.label}</h3>
              <p className="text-sm text-muted-foreground">{statusConfig.description}</p>
              {isConnected && status.connectedAt && (
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Activity className="h-3 w-3" />
                  Connected {formatDistanceToNow(new Date(status.connectedAt), { addSuffix: true })}
                </p>
              )}
              {!isConnected && statusConfig.label == unknown_status.label && status.disconnectedAt && (
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Last seen {formatDistanceToNow(new Date(status.disconnectedAt), { addSuffix: true })}
                </p>
              )}
            </div>
          </div>

          {/* Middle: HA Warning (only when multiple connections) */}
          {hasMultipleConnections && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-amber-300">Multiple Connections Detected</p>
                <p className="text-xs text-amber-200/70 mt-1">
                  {status.connections.length} connections indicate High Availability mode.<br />
                  Verify this configuration is intentional.
                </p>
              </div>
            </div>
          )}

          {/* Right: Active Connections */}
          {hasConnections && (
            <div className="lg:border-l lg:border-muted/30 lg:pl-4 lg:min-w-[240px]">
              <div className="flex items-center gap-1.5 mb-2">
                <Badge variant="outline" className={cn(statusConfig.badgeClass, "text-xs")}>
                  {isConnected ? <Wifi className="h-3 w-3 mr-1" /> : <WifiOff className="h-3 w-3 mr-1" />}
                  {status.connections.length} connection{status.connections.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              <div className="space-y-1.5">
                {status.connections.map((conn, idx) => (
                  <div
                    key={conn.clientUuid || idx}
                    className="flex items-center justify-between px-2.5 py-1.5 rounded bg-zinc-900/60 border border-zinc-800 text-sm"
                  >
                    <span className="font-mono text-xs">{conn.originIp || 'Unknown IP'}</span>
                    <Badge variant="outline" className="font-mono text-[10px] h-5 px-1.5">
                      v{conn.clientVersion || '?'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
